export interface ShrnaProductSearch {
  term: string
  count: number
  page: number
  region: string
  facet: string[]
  locale: string
  sort: string
  order: string
  store: string
}

export interface ShrnaProductSearchResponseData {
  requestDate: number
  responseDate: number
  version: number
  term: string[]
  count: number
  cloneInformation: CloneInformation[]
}

export interface CloneInformation {
  fullName: string
  geneSymbol: string
  geneId: string
  knockdown: string
  species: string
  targetSeq: string
  method: string
  trcVersion: string
  oligoSeq: string
  validated: string
  trcId: string
  cellType: string
  cloneName: string
  synonyms: string[]
  region: string
  refSeq: string
  searchTrcVersion: string
  price: number
  currency: string
  __typename?: string
}

export interface ShrnaDropDownSearch {
  countryCode: string
  physicalMaterial: string
  amount: string
  titer: string
  volume: string
  aliquot: string
  store: string
}

export interface ShrnaDropDownResponse {
  physicalMaterial: string[]
  amount: string[]
  titer: string[]
  volume: string[]
  aliquot: string[]
  vector: string[]
}

export interface SpecificationValues {
  physicalMaterial: string
  amount: string
  titer: string
  volume: string
  aliquot: string
  vector: string
}

export interface PriceCallData {
  cloneInformation: CloneInformation[]
  physicalFormat: PhysicalFormatItem
  country: string
}

export interface PhysicalFormatItem {
  physicalMaterial: string
  amount: string
  titer: string
  volume: string
  aliquot: string
  vector: string
}

export interface CloneInformation {
  fullName: string
  geneSymbol: string
  geneId: string
  knockdown: string
  species: string
  targetSeq: string
  method: string
  trcVersion: string
  oligoSeq: string
  validated: string
  trcId: string
  cellType: string
  cloneName: string
  synonyms: string[]
  region: string
  refSeq: string
  searchTrcVersion: string
  price: number
  currency: string
  __typename?: string
}

export interface PriceCallResponse {
  shrnaResult: ShrnaPriceCallResult[]
}
export interface ShrnaPriceCallResult {
  fullName: string
  geneSymbol: string
  geneId: string
  knockdown: string
  species: string
  targetSeq: string
  method: string
  trcVersion: string
  oligoSeq: string
  validated: string
  trcId: string
  cellType: string
  cloneName: string
  synonyms: string[]
  region: string
  refSeq: string
  searchTrcVersion: string
  price: number
  currency: string
  brand: string
  material: string
  currencySymbol: string
  __typename?: string
}

export interface PaginationData {
  totalCount: number
  page: number
  reagentItemsCount: number
  perPageCount: number
}

export interface SpeciesCheckboxes {
  mouse: boolean
  human: boolean
}

export interface TrcCheckboxes {
  trc1: boolean
  trc2: boolean
}

export interface ShrnaAddtoCartData {
  priceCallData: PriceCallData
  sapNextEnabled: boolean
  locale: string
  timeZone: string
  store: string
}

export interface CheckLinks {
  itemSelectionLink: boolean
  specificationLink: boolean
}

export interface SearchState {
  searchValue: ShrnaProductSearch
  pageError: string
  searchResponseJson: ShrnaProductSearchResponseData
  shrnaDropDownValue: ShrnaDropDownSearch
  shrnaDropDownResponseList: ShrnaDropDownResponse
  specificationValues: SpecificationValues
  priceCallData: PriceCallData
  priceCallResponse: PriceCallResponse
  paginationData: PaginationData
  stepNo: number
  speciesCheckboxes: SpeciesCheckboxes
  trcCheckboxes: TrcCheckboxes
  traceOnClick: boolean
  checkFileUpload: boolean
  shrnaAddtoCartData: ShrnaAddtoCartData
  checkActiveLink: CheckLinks
  setIsAllChecked: boolean
  setPage: number
  perPageCount: string
}

export const initialState: SearchState = {
  searchValue: {
    term: '',
    count: 10,
    page: 1,
    region: '',
    facet: [
      'species:Human',
      'species:Mouse',
      'trc:1',
      'trc:2',
      'validated:Yes',
      'validated:No',
    ],
    locale: '',
    sort: 'trcId',
    order: 'ASC',
    store: '',
  },
  pageError: '',
  perPageCount: '10',
  searchResponseJson: {
    requestDate: 0,
    responseDate: 0,
    version: 0,
    term: [],
    count: 0,
    cloneInformation: [],
  },
  shrnaDropDownValue: {
    countryCode: '',
    physicalMaterial: '',
    amount: '',
    titer: '',
    volume: '',
    aliquot: '',
    store: '',
  },
  shrnaDropDownResponseList: {
    physicalMaterial: [],
    amount: [],
    titer: [],
    volume: [],
    aliquot: [],
    vector: [],
  },
  specificationValues: {
    physicalMaterial: '',
    amount: '',
    titer: '',
    volume: '',
    aliquot: '',
    vector: '',
  },
  priceCallData: {
    cloneInformation: [],
    country: '',
    physicalFormat: {
      physicalMaterial: '',
      amount: '',
      titer: '',
      volume: '',
      aliquot: '',
      vector: '',
    },
  },
  priceCallResponse: {
    shrnaResult: [],
  },
  paginationData: {
    totalCount: 0,
    page: 1,
    reagentItemsCount: 0,
    perPageCount: 1,
  },
  stepNo: 0,
  speciesCheckboxes: {
    human: true,
    mouse: true,
  },
  trcCheckboxes: {
    trc1: true,
    trc2: true,
  },
  traceOnClick: false,
  checkFileUpload: false,
  setIsAllChecked: false,
  setPage: 1,
  shrnaAddtoCartData: {
    priceCallData: {
      cloneInformation: [],
      country: '',
      physicalFormat: {
        physicalMaterial: '',
        amount: '',
        titer: '',
        volume: '',
        aliquot: '',
        vector: 'PLKO.1',
      },
    },
    sapNextEnabled: false,
    locale: '',
    timeZone: '',
    store: '',
  },
  checkActiveLink: {
    itemSelectionLink: false,
    specificationLink: false,
  },
}

type Action =
  | { type: 'INIT' }
  | { type: 'UPDATE_SEARCH_TERM'; data: ShrnaProductSearch }
  | { type: 'UPDATE_SEARCH_RESPONSE'; data: any }
  | { type: 'SET_PAGE_ERROR'; data: string }
  | { type: 'UPDATE_DROP_DOWN'; data: ShrnaDropDownSearch }
  | { type: 'UPDATE_DROPDOWN_RESPONSE'; data: any }
  | { type: 'UPDATE_SPECIFICATION_VALUES'; data: SpecificationValues }
  | { type: 'UPDATE_PRICE_CALL'; data: PriceCallData }
  | { type: 'UPDATE_PRICE_CALL_RESPONSE'; data: any }
  | { type: 'UPDATE_PAGINATION_DATA'; data: PaginationData }
  | { type: 'SET_STEP_NO'; data: number }
  | { type: 'UPDATE_SPECIES_CHECK_BOX'; data: SpeciesCheckboxes }
  | { type: 'UPDATE_TRC_CHECK_BOX'; data: TrcCheckboxes }
  | { type: 'TRACE_ON_CLICK'; data: boolean }
  | { type: 'CHECK_FILE_UPLOAD'; data: boolean }
  | { type: 'SET_INITIAL_STATE'; data: SearchState }
  | { type: 'UPDATE_ADD_TO_CART_DATA'; data: ShrnaAddtoCartData }
  | { type: 'CHECK_ACTIVE_LINK'; data: CheckLinks }
  | { type: 'SET_IS_ALL_CHECKED'; data: boolean }
  | { type: 'SET_PAGINATION_PAGE'; data: number }
  | { type: 'SET_PER_PAGE_COUNT'; data: string }

export interface ContextProps {
  state: SearchState
  dispatch: ({ type, data }) => void
}

export function reducer(state: SearchState, action: Action): SearchState {
  const keys = {
    UPDATE_SEARCH_TERM: 'searchValue',
    SET_PAGE_ERROR: 'pageError',
    SET_PER_PAGE_COUNT: 'perPageCount',
    UPDATE_SEARCH_RESPONSE: 'searchResponseJson',
    UPDATE_PRICE_CALL: 'priceCallData',
    UPDATE_DROPDOWN_RESPONSE: 'shrnaDropDownResponseList',
    UPDATE_SPECIFICATION_VALUES: 'specificationValues',
    UPDATE_DROP_DOWN: 'shrnaDropDownValue',
    UPDATE_PRICE_CALL_RESPONSE: 'priceCallResponse',
    UPDATE_PAGINATION_DATA: 'paginationData',
    SET_IS_ALL_CHECKED: 'setIsAllChecked',
    SET_PAGINATION_PAGE: 'setPage',
    SET_STEP_NO: 'stepNo',
    UPDATE_SPECIES_CHECK_BOX: 'speciesCheckboxes',
    CHECK_ACTIVE_LINK: 'checkActiveLink',
    UPDATE_TRC_CHECK_BOX: 'trcCheckboxes',
    TRACE_ON_CLICK: 'traceOnClick',
    CHECK_FILE_UPLOAD: 'checkFileUpload',
    UPDATE_ADD_TO_CART_DATA: 'shrnaAddtoCartData',
  }

  if (keys[action.type] && action.type !== 'INIT') {
    const newState = { ...state }
    newState[keys[action.type]] = action.data
    return newState
  } else {
    return initialState
  }
}
